import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { STORAGE_KEYS } from '../../../constants'
import { Dict } from '../../../globalTypes'
import persistentStorage from '../../../helpers/PersistentStorage'
import { Button } from '../../buttons/Button/Button'
import { StepForm, StepFormStep } from '../../form/StepForm/StepForm'
import { Modal } from '../../misc/Modal/Modal'
import * as Styled from '../styled'
import {
  AnalyticsPageEventInfo,
  AnalyticsTrackEventInfo,
  ProductWizardType,
  SubmitOptionsType,
  UpdateCartItemParams,
  WizardData,
} from '../types'
import StepChooseItem from './components/StepChooseItem/StepChooseItem'
import StepQuestionnaire from './components/StepQuestionnaire/StepQuestionnaire'
import StepResult from './components/StepResult/StepResult'
import StepSelectProcoteWizard from './components/StepSelectProcote/StepSelectProcote'
import StepYieldAndPounds from './components/StepYieldAndPounds/StepYieldAndPounds'
import { getProcoteSubmitOptions } from './helpers/getProcoteSubmitOptions'
import { getProcoteUpdateCartItems } from './helpers/getProcoteUpdateCartItems'
import {
  ProcoteQuestionnaireType,
  ProcoteStepNameForAnalytics,
  ProcoteWizardNode,
  ProductWizardProcoteSteps,
} from './types'

const data: ProcoteQuestionnaireType = persistentStorage.getLocalData(STORAGE_KEYS.ProcoteQuestionnaire)

export type BasicProduct = {
  id: string
  title: string
  description?: string
}

export type ProcoteProps<AllowChooseExactProduct extends boolean = false> = {
  opened: boolean
  onClose: () => void
  handleProductId?: (string) => void
  isMobile?: boolean
  handleGoToCart?: () => void
  handleMarketAddProduct?: (id: string, options?: SubmitOptionsType<ProductWizardType.Procote>) => void
  handleMarketUpdateProduct?: (params: UpdateCartItemParams<ProductWizardType.Procote>) => void
  productWizard: ProcoteWizardNode
  handleCrm?: (id: string, qty: number, comment?: string, data?: WizardData<ProductWizardType.Procote>) => void
  onSendAnalyticsPage?: (info: AnalyticsPageEventInfo) => void
  onSendAnalyticsTrack?: (info: AnalyticsTrackEventInfo) => void
  allowChooseExactProduct?: AllowChooseExactProduct
} & (AllowChooseExactProduct extends true
  ? {
      products: BasicProduct[]
    }
  : {
      products?: BasicProduct[]
    })

export const Procote = <AllowChooseExactProduct extends boolean = false>({
  handleCrm,
  productWizard,
  opened,
  onClose,
  isMobile,
  handleGoToCart,
  handleMarketAddProduct,
  handleMarketUpdateProduct,
  handleProductId,
  products = [],
  allowChooseExactProduct,
  onSendAnalyticsPage,
  onSendAnalyticsTrack,
}: ProcoteProps<AllowChooseExactProduct>) => {
  const { t } = useTranslation('procote')

  const [step, setStep] = useState('')
  const [showQuestionnaire, setShowQuestionnaire] = useState(false)
  const [wizardNodeId, setWizardNodeId] = useState<string | null>(null)
  // Actually it's wizard's node title, but it's kinda confusing to name it as
  // wizardNodeTitle because procote wizard nodes is named as categories
  const [categoryName, setCategoryName] = useState<string | null>(null)
  const [expectedYield, setExpectedYield] = useState(0)
  const [yieldUnit, setYieldUnit] = useState('')
  const [fertilizer, setFertilizer] = useState(0)
  const [acres, setAcres] = useState(0)
  const [productId, setProductId] = useState('')
  const [productName, setProductName] = useState('')
  const [qty, setQty] = useState(0)
  const [userKnowsWhichProductIsNeeded, setUserKnowsWhichProductIsNeeded] = useState<boolean | null>(null)
  const [litresPerTonne, setLitresPerTonne] = useState(0)

  const nodesDict = useMemo(() => {
    const result: Dict<ProcoteWizardNode> = {}

    productWizard.children.forEach(node => {
      result[node.id] = node
    })

    return result
  }, [productWizard])

  const getSelectedNode = () => (wizardNodeId ? nodesDict[wizardNodeId] : null)

  const getItems = (): (BasicProduct | ProcoteWizardNode)[] => {
    return allowChooseExactProduct
      ? userKnowsWhichProductIsNeeded
        ? products
        : productWizard.children
      : productWizard.children
  }

  const getWizardProducts = (): ProcoteWizardNode[] => {
    return getSelectedNode()?.children ?? []
  }

  const handleWizardClose = () => {
    setUserKnowsWhichProductIsNeeded(null)
    setWizardNodeId(null)
    setCategoryName(null)
    setExpectedYield(0)
    setFertilizer(0)
    setAcres(0)
    setProductId('')
    setProductName('')
    setQty(0)
    setStep('')
    onClose()
  }

  const handleStepChooseItem = (item: BasicProduct | ProcoteWizardNode) => {
    setStep(ProductWizardProcoteSteps.YieldAndPounds)

    if (!userKnowsWhichProductIsNeeded || !allowChooseExactProduct) {
      setWizardNodeId(item.id)
      setCategoryName(item.title)
    } else {
      setProductId(item.id)
      setProductName(item.title)
    }
  }

  const handleStepResult = (qty: number, id?: string, name?: string) => {
    id && setProductId(id)
    qty && setQty(qty)
    name && setProductName(name)
    id && handleProductId && handleProductId(id)
  }

  const haveFertilizer = !!data?.[productId]

  const handleAddToCart = () => {
    onSendAnalyticsTrack?.({
      step: ProcoteStepNameForAnalytics.Result,
      choice: 'add_to_cart',
    })

    const params = getProcoteSubmitOptions({
      qty,
      t,
      yieldUnit,
      litresPerTonne,
      categoryName,
      fertilizer,
      expectedYield,
      acres,
      productId,
      productName,
      haveFertilizer,
      steps: wizardNodeId ? [wizardNodeId] : [],
    })

    if (handleMarketAddProduct) {
      handleMarketAddProduct(productId, params)
      setShowQuestionnaire(true)
      onClose()
      setStep(ProductWizardProcoteSteps.ChooseCrop)
    } else if (handleCrm) {
      handleCrm(productId, qty, params.wizard_comment, params.wizard_data)
      handleWizardClose()
    }
  }

  const handleSubmitQuestionnaire = (haveFertilizerArg: boolean) => {
    const updatedParams = getProcoteUpdateCartItems({
      t,
      yieldUnit,
      litresPerTonne,
      categoryName,
      fertilizer,
      expectedYield,
      acres,
      productId,
      productName,
      haveFertilizer: haveFertilizerArg,
      steps: wizardNodeId ? [wizardNodeId] : [],
    })

    persistentStorage.changeItemFromLocalData(STORAGE_KEYS.ProcoteQuestionnaire, productId, haveFertilizerArg)

    handleMarketUpdateProduct && handleMarketUpdateProduct(updatedParams)

    handleWizardClose()
    handleGoToCart && handleGoToCart()
  }

  const handleExpectedYield = (expectedYield: number, unit?: string) => {
    setExpectedYield(expectedYield)
    unit && setYieldUnit(unit)
  }

  const mapStepNames = () => {
    switch (true) {
      case step === ProductWizardProcoteSteps.ChooseItem && userKnowsWhichProductIsNeeded:
        return ProcoteStepNameForAnalytics.ChooseProcote
      case step === ProductWizardProcoteSteps.ChooseItem && !userKnowsWhichProductIsNeeded:
        return ProcoteStepNameForAnalytics.ChooseCrop
      case step === ProductWizardProcoteSteps.YieldAndPounds:
        return ProcoteStepNameForAnalytics.EnterPounds
      case step === ProductWizardProcoteSteps.Result:
        return ProcoteStepNameForAnalytics.Result
      default:
        return ProcoteStepNameForAnalytics.Unknown
    }
  }

  return (
    <>
      {allowChooseExactProduct && (
        <StepSelectProcoteWizard
          opened={!userKnowsWhichProductIsNeeded ? opened : false}
          onClose={handleWizardClose}
          isMobile={isMobile}
          setUserKnowsWhichProductIsNeeded={setUserKnowsWhichProductIsNeeded}
          setStep={setStep}
          onSendAnalyticsPage={onSendAnalyticsPage}
          onSendAnalyticsTrack={onSendAnalyticsTrack}
        />
      )}
      <Modal
        isOpen={!!step || (opened && !allowChooseExactProduct)}
        onClose={() => {
          handleWizardClose()
          onSendAnalyticsTrack?.({
            step: mapStepNames(),
            action: 'close',
          })
        }}
        heading={t('title')}
        size={isMobile ? 'medium' : 'responsive'}
        modalPadding={'noPadding'}
      >
        <StepForm
          step={step ? step : !allowChooseExactProduct ? ProductWizardProcoteSteps.ChooseItem : ''}
          completed={false}
          onTabClick={() => undefined}
          headerSize={'small'}
        >
          <StepFormStep
            id={ProductWizardProcoteSteps.ChooseItem}
            title={userKnowsWhichProductIsNeeded ? t('stepForm.chooseProcoteTitle') : t('stepForm.chooseCropTitle')}
          >
            <Styled.StepWrapper>
              <StepChooseItem
                getItems={getItems}
                handleNextStep={handleStepChooseItem}
                onSendAnalyticsPage={onSendAnalyticsPage}
                onSendAnalyticsTrack={onSendAnalyticsTrack}
                userKnowsWhichProductIsNeeded={!!userKnowsWhichProductIsNeeded}
              />
              {allowChooseExactProduct && (
                <Styled.ButtonsWrapper>
                  <Styled.BackButton
                    onClick={() => {
                      setStep('')
                      setUserKnowsWhichProductIsNeeded(null)
                      onSendAnalyticsTrack?.({
                        step:
                          userKnowsWhichProductIsNeeded && allowChooseExactProduct
                            ? ProcoteStepNameForAnalytics.ChooseProcote
                            : ProcoteStepNameForAnalytics.ChooseCrop,
                        action: 'back',
                      })
                    }}
                  />
                </Styled.ButtonsWrapper>
              )}
            </Styled.StepWrapper>
          </StepFormStep>
          <StepFormStep
            id={ProductWizardProcoteSteps.YieldAndPounds}
            title={userKnowsWhichProductIsNeeded ? t('stepForm.poundsTitle') : t('stepForm.yieldAndPoundsTitle')}
          >
            <Styled.StepWrapper>
              <StepYieldAndPounds
                getSelectedNode={getSelectedNode}
                expectedYield={expectedYield}
                setExpectedYield={handleExpectedYield}
                fertilizer={fertilizer}
                setFertilizer={setFertilizer}
                acres={acres}
                setAcres={setAcres}
                userKnowsWhichProductIsNeeded={!!userKnowsWhichProductIsNeeded}
                onSendAnalyticsPage={onSendAnalyticsPage}
              />
              <Styled.ButtonsWrapper>
                <Styled.BackButton
                  onClick={() => {
                    setStep(ProductWizardProcoteSteps.ChooseItem)
                    onSendAnalyticsTrack?.({
                      step: ProcoteStepNameForAnalytics.EnterPounds,
                      action: 'back',
                    })
                  }}
                />
                <Button
                  data-test-id={'next-btn'}
                  intent={'primary'}
                  filled
                  onClick={() => {
                    setStep(ProductWizardProcoteSteps.Result)
                    onSendAnalyticsTrack?.({
                      step: ProcoteStepNameForAnalytics.EnterPounds,
                      choice: `expected_yield: ${expectedYield}, fertilizer: ${fertilizer}, acres: ${acres}`,
                    })
                  }}
                  disabled={(!userKnowsWhichProductIsNeeded && !expectedYield) || !fertilizer || !acres}
                >
                  {t('stepForm.continue')}
                </Button>
              </Styled.ButtonsWrapper>
            </Styled.StepWrapper>
          </StepFormStep>
          <StepFormStep id={ProductWizardProcoteSteps.Result} title={t('stepForm.resultTitle')}>
            <Styled.StepWrapper>
              <StepResult
                getSelectedNode={getSelectedNode}
                getWizardProducts={getWizardProducts}
                expectedYield={expectedYield}
                fertilizer={fertilizer}
                acres={acres}
                categoryName={categoryName}
                handleResult={handleStepResult}
                productId={productId}
                products={products}
                userKnowsWhichProductIsNeeded={!!userKnowsWhichProductIsNeeded}
                setLitresPerTonne={setLitresPerTonne}
                onSendAnalyticsPage={onSendAnalyticsPage}
              />
              <Styled.ButtonsWrapper>
                <Styled.BackButton
                  onClick={() => {
                    setStep(ProductWizardProcoteSteps.YieldAndPounds)
                    onSendAnalyticsTrack?.({
                      step: ProcoteStepNameForAnalytics.Result,
                      action: 'back',
                    })
                  }}
                />
                <Button data-test-id={'next-btn'} intent={'primary'} filled onClick={handleAddToCart}>
                  {t('stepForm.addToCart')}
                </Button>
              </Styled.ButtonsWrapper>
            </Styled.StepWrapper>
          </StepFormStep>
        </StepForm>
      </Modal>
      {showQuestionnaire && (
        <StepQuestionnaire
          opened={showQuestionnaire}
          onClose={() => {
            handleWizardClose()
            setShowQuestionnaire(false)
          }}
          isMobile={isMobile}
          value={haveFertilizer}
          onSubmit={handleSubmitQuestionnaire}
          onGoToCart={() => {
            handleWizardClose()
            handleGoToCart && handleGoToCart()
          }}
          productName={productName}
          qty={qty}
          onSendAnalyticsPage={onSendAnalyticsPage}
          onSendAnalyticsTrack={onSendAnalyticsTrack}
        />
      )}
    </>
  )
}
